import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  Nullable,
  UserWithToken,
  WithData,
  WithMeta,
} from '@ygoargentina/sdk/api/api.types';

// =============================================================================
// TYPES
// =============================================================================

export type DeckListingType = 'FULL_DECK' | 'DECK_BASE';
export type DeckListingStatus =
  | 'PUBLISHED'
  | 'PAUSED'
  | 'OUTDATED'
  | 'DELETED'
  // Un mazo es único: al comprarse deja de estar publicado.
  | 'SOLD';

export interface DeckListingCard {
  id: number;
  konamiId: number;
  deckType: string;
  quantity: number;
  card?: {
    konamiId: number;
    name: string;
    image?: string;
    type?: string;
    frameType?: string | null;
  };
}

export interface DeckListing {
  id: number;
  title: string;
  description?: string;
  type: DeckListingType;
  price?: number | null;
  tradeOnly: boolean;
  status: DeckListingStatus;
  cards: DeckListingCard[];
  mainDeck: DeckListingCard[];
  extraDeck: DeckListingCard[];
  sideDeck: DeckListingCard[];
  cardsCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface DeckListingCardInput {
  konamiId: number;
  quantity: number;
}

export interface CreateDeckListingInput {
  title: string;
  description?: string;
  type: DeckListingType;
  price?: number | null;
  tradeOnly: boolean;
  mainDeck: DeckListingCardInput[];
  extraDeck?: DeckListingCardInput[];
  sideDeck?: DeckListingCardInput[];
}

export interface UpdateDeckListingInput {
  title?: string;
  description?: string;
  type?: DeckListingType;
  price?: number | null;
  tradeOnly?: boolean;
  mainDeck?: DeckListingCardInput[];
  extraDeck?: DeckListingCardInput[];
  sideDeck?: DeckListingCardInput[];
}

export interface PublicDeckListing {
  id: number;
  title: string;
  description?: string;
  type: DeckListingType;
  price?: number | null;
  tradeOnly: boolean;
  cardsCount: number;
  seller: {
    id: number;
    name: string;
    lastName: string;
    picture: string | null;
    city: string | null;
    province: { id: number; name: string } | null;
  };
  createdAt: string;
}

export interface PublicDeckListingDetail extends PublicDeckListing {
  mainDeck: DeckListingCard[];
  extraDeck: DeckListingCard[];
  sideDeck: DeckListingCard[];
}

export interface PublicDeckListingsFilters {
  search?: string;
  type?: DeckListingType;
  minPrice?: number;
  maxPrice?: number;
  tradeOnly?: boolean;
  page?: number;
  limit?: number;
}

// =============================================================================
// FRAGMENTS
// =============================================================================

const DECK_LISTING_CARD_FRAGMENT = gql`
  fragment DeckListingCardFields on DeckListingCard {
    id
    konamiId
    deckType
    quantity
    card {
      konamiId
      name
      image
      type
      frameType
    }
  }
`;

const DECK_LISTING_FRAGMENT = gql`
  ${DECK_LISTING_CARD_FRAGMENT}
  fragment DeckListingFields on DeckListing {
    id
    title
    description
    type
    price
    tradeOnly
    status
    cardsCount
    cards {
      ...DeckListingCardFields
    }
    mainDeck {
      ...DeckListingCardFields
    }
    extraDeck {
      ...DeckListingCardFields
    }
    sideDeck {
      ...DeckListingCardFields
    }
    createdAt
    updatedAt
  }
`;

// =============================================================================
// QUERIES (Authenticated)
// =============================================================================

export const getMyDeckListings = async (
  user: Nullable<UserWithToken>,
  variables?: { status?: DeckListingStatus; page?: number; limit?: number }
) =>
  getGraphQLQueryClient(user).request<WithMeta<DeckListing>>(
    gql`
      ${DECK_LISTING_FRAGMENT}
      query myDeckListings($status: DeckListingStatus, $page: Int, $limit: Int) {
        data: myDeckListings(status: $status, page: $page, limit: $limit) {
          data {
            ...DeckListingFields
          }
          meta {
            total
            perPage
            lastPage
            page
          }
        }
      }
    `,
    variables
  );

export const getDeckListing = async (
  user: Nullable<UserWithToken>,
  variables: { id: number }
) =>
  getGraphQLQueryClient(user).request<WithData<DeckListing | null>>(
    gql`
      ${DECK_LISTING_FRAGMENT}
      query deckListing($id: Int!) {
        data: deckListing(id: $id) {
          ...DeckListingFields
        }
      }
    `,
    variables
  );

// =============================================================================
// QUERIES (Public)
// =============================================================================

export const getPublicDeckListings = async (
  filters?: PublicDeckListingsFilters
) =>
  getGraphQLQueryClient(null).request<WithMeta<PublicDeckListing>>(
    gql`
      query publicDeckListings($filters: PublicDeckListingsFilters) {
        data: publicDeckListings(filters: $filters) {
          data {
            id
            title
            description
            type
            price
            tradeOnly
            cardsCount
            seller {
              id
              name
              lastName
              picture
              city
              province {
                id
                name
              }
            }
            createdAt
          }
          meta {
            total
            perPage
            lastPage
            page
          }
        }
      }
    `,
    { filters }
  );

export const getPublicDeckListing = async (id: number) =>
  getGraphQLQueryClient(null).request<WithData<PublicDeckListingDetail | null>>(
    gql`
      ${DECK_LISTING_CARD_FRAGMENT}
      query publicDeckListing($id: Int!) {
        data: publicDeckListing(id: $id) {
          id
          title
          description
          type
          price
          tradeOnly
          cardsCount
          mainDeck {
            ...DeckListingCardFields
          }
          extraDeck {
            ...DeckListingCardFields
          }
          sideDeck {
            ...DeckListingCardFields
          }
          seller {
            id
            name
            lastName
            picture
            city
            province {
              id
              name
            }
          }
          createdAt
        }
      }
    `,
    { id }
  );

// =============================================================================
// MUTATIONS
// =============================================================================

export const createDeckListing = async (
  user: Nullable<UserWithToken>,
  variables: { input: CreateDeckListingInput }
) =>
  getGraphQLQueryClient(user).request<WithData<DeckListing>>(
    gql`
      ${DECK_LISTING_FRAGMENT}
      mutation createDeckListing($input: CreateDeckListingInput!) {
        data: createDeckListing(input: $input) {
          ...DeckListingFields
        }
      }
    `,
    variables
  );

export const updateDeckListing = async (
  user: Nullable<UserWithToken>,
  variables: { id: number; input: UpdateDeckListingInput }
) =>
  getGraphQLQueryClient(user).request<WithData<DeckListing>>(
    gql`
      ${DECK_LISTING_FRAGMENT}
      mutation updateDeckListing($id: Int!, $input: UpdateDeckListingInput!) {
        data: updateDeckListing(id: $id, input: $input) {
          ...DeckListingFields
        }
      }
    `,
    variables
  );

export const pauseDeckListing = async (
  user: Nullable<UserWithToken>,
  variables: { id: number }
) =>
  getGraphQLQueryClient(user).request<WithData<DeckListing>>(
    gql`
      ${DECK_LISTING_FRAGMENT}
      mutation pauseDeckListing($id: Int!) {
        data: pauseDeckListing(id: $id) {
          ...DeckListingFields
        }
      }
    `,
    variables
  );

export const activateDeckListing = async (
  user: Nullable<UserWithToken>,
  variables: { id: number }
) =>
  getGraphQLQueryClient(user).request<WithData<DeckListing>>(
    gql`
      ${DECK_LISTING_FRAGMENT}
      mutation activateDeckListing($id: Int!) {
        data: activateDeckListing(id: $id) {
          ...DeckListingFields
        }
      }
    `,
    variables
  );

export const deleteDeckListing = async (
  user: Nullable<UserWithToken>,
  variables: { id: number }
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation deleteDeckListing($id: Int!) {
        data: deleteDeckListing(id: $id)
      }
    `,
    variables
  );
